/** Pure solo-run helpers: where a run's phase sends the player, and the resume card copy (screens §P1 solo). */
import type { ContentCatalog, ScoreTier, SoloRunState, TeamState } from "@bsim/contracts";
import { CHAIR_STEPS } from "../shell/guide";
import { deskLabeler, type DeskLabeler } from "../decisions/labels";
import { formatMetric } from "@gxs/metric-defs";
import { formatUsdPerShare } from "../format";
import { TIER_LABELS } from "../score/labels";

/**
 * The chair step a player still owes, or null once the chair is taken: a round-1 run with no saved statement owes Strategy; with a
 * statement but no limits saved yet it owes Risk appetite; with the limits saved it owes the practice quarter (the run keeps no record of that one, so a player who took it is
 * asked once more at most). From round 2, or once the draft has been edited, the chair is behind them.
 */
export function chairStepOwed(team: Pick<TeamState, "strategyStatement" | "draft" | "round">): number | null {
  if (team.round === null || team.round.roundNo > 1 || (team.draft?.version ?? 0) > 0) return null;
  return team.strategyStatement === null ? 0 : (team.strategyStatement.riskAppetite?.length ?? 0) === 0 ? 1 : 2;
}

/**
 * A solo run IS a cohort (runId == cohortId): play screens are the ordinary participant routes. A deciding run goes to the chair step it
 * still owes (`chairStep`, from `chairStepOwed`); without it, a run nobody has touched opens on the chair and any other resumes in Decide.
 */
export function playPath(state: Pick<SoloRunState, "runId" | "phase" | "round" | "resume">, chairStep?: number | null): string {
  switch (state.phase) {
    case "completed": return `/solo/${state.runId}/final`;
    case "resolving": case "reviewing": case "awaiting_event": return `/solo/${state.runId}`;
    case "deciding": {
      const step = chairStep !== undefined ? chairStep : state.round.roundNo === 0 || (state.round.roundNo === 1 && state.resume === null) ? 0 : null;
      return step === null ? `/c/${state.runId}/decide` : `/c/${state.runId}/chair?step=${step}`;
    }
  }
}

const joinAnd = (items: readonly string[]): string => items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1] ?? ""}`;

/** "Last time: Round 3. You changed Growth and Deposits. An event is waiting." from `SoloRunState.resume` only. */
export function resumeText(resume: NonNullable<SoloRunState["resume"]>, desk: DeskLabeler = deskLabeler(undefined)): string {
  const parts = [
    resume.lastSubmittedRoundNo === null ? null : `Last time: Round ${resume.lastSubmittedRoundNo}.`,
    resume.lastChangedFamilies.length === 0 ? null : `You changed ${joinAnd(resume.lastChangedFamilies.map(desk))}.`,
    resume.eventPending ? "An event is waiting." : null,
  ].filter((p): p is string => p !== null);
  return parts.length === 0 ? "Pick up where you left off." : parts.join(" ");
}

/** The resume card's line when the chair is not finished: it names the step. */
// The run keeps no record of a finished practice step, so past Strategy the line names what lies ahead, never one step as the place left off.
export const chairResumeText = (step: number): string => step === 0 ? `Pick up where you left off: ${CHAIR_STEPS[0]}.` : step === 1 ? "Your strategy is saved. Before round 1, check your risk appetite and take the practice quarter." : "Your strategy and limits are saved. Before round 1, take the practice quarter.";
export const resumeLabel = (step: number | null): string => step === null ? "Resume" : `Resume \u00b7 ${CHAIR_STEPS[step] ?? CHAIR_STEPS[0]}`;


/** The program's target tier (content §2.5): a tier is value created vs your status-quo twin, never a rank. */
const GOAL_TIER: ScoreTier = "topQuartile";
const TIERS_LOW_TO_HIGH: readonly ScoreTier[] = ["behindTwin", "steward", "topQuartile", "strong"];
/** The tier's floor in points ("2"), from the catalog's `tierFloorsRate` (ADR-0042); null for the tier below every floor, or a catalog without it. */
export const tierFloorPp = (catalog: Pick<ContentCatalog, "tierFloorsRate">, tier: ScoreTier): string | null => {
  const floor = tier === "behindTwin" ? undefined : catalog.tierFloorsRate?.[tier];
  return floor === undefined ? null : String(Math.round(floor * 10_000) / 100);
};
/** The goal in one plain sentence (owner note 1): value, not a tier. The tier is the target, shown as "Your goal" on its row of the ladder. */
export const GOAL_LINE = "Create more shareholder value than your twin.";

export interface TierRow {
  readonly tier: Exclude<ScoreTier, "behindTwin">;
  readonly name: string;
  /** "0", "+1.75": the floor in percentage points of the twin's value per share. */
  readonly points: string;
  /** "Level with your twin" for the floor of 0, else "Ahead of your twin by". */
  readonly lead: string;
  /** "$0.00 a share", "$1.10 a share"; null when no base is known. */
  readonly dollars: string | null;
  readonly goal: boolean;
}

/**
 * The tier ladder with its dollars: each floor (`tierFloorsRate`, a rate of the twin's value per share) times the base, the value of one hundred points a share:
 * the scorecard's conversion base (`valueCreatedBaseUsdPerShare`) for a finished run, the twin's start price before one exists.
 * Null without the catalog's floors; `dollars` are null without a base (the page then says what a point is, not what it is worth).
 */
export function tierLadder(catalog: Pick<ContentCatalog, "tierFloorsRate">, baseUsdPerShare: number | null): readonly TierRow[] | null {
  const floors = catalog.tierFloorsRate;
  if (floors === undefined) return null;
  return (["steward", "topQuartile", "strong"] as const).map((tier): TierRow => {
    const floor = floors[tier];
    const usd = baseUsdPerShare === null ? null : floor * baseUsdPerShare;
    return {
      tier, name: TIER_LABELS[tier], goal: tier === GOAL_TIER,
      points: floor <= 0 ? "0" : formatMetric(floor * 100, "index", { precision: 2, signed: true }),
      lead: floor <= 0 ? "Level with your twin" : "Ahead of your twin by",
      dollars: usd === null ? null : floor <= 0 ? `${formatUsdPerShare(0)} a share` : `${formatUsdPerShare(usd)} a share`,
    };
  });
}

export const GOAL_TIER_LABEL = TIER_LABELS[GOAL_TIER];
export const goalReached = (tier: ScoreTier): boolean => TIERS_LOW_TO_HIGH.indexOf(tier) >= TIERS_LOW_TO_HIGH.indexOf(GOAL_TIER);
